import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  DestroyRef,
  inject,
  input,
  InputSignal,
  model,
  ModelSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ModalComponent } from '@appkit4/angular-components/modal';
import { RichEdit } from 'devexpress-richedit';
import { DataMode } from '../../helpers/enums/data-mode.enum';
import { RichEditorService } from '../../services/rich-editor.service';
import { EditorComponent } from '../editor/editor.component';

@Component({
  standalone: false,
  selector: 'gstar-extended-editor',
  templateUrl: './extended-editor.component.html',
  styleUrl: './extended-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExtendedEditorComponent implements AfterViewInit, OnDestroy {
  private readonly activatedRouter = inject(ActivatedRoute);
  private readonly activeRichEditor = inject(RichEditorService);
  private readonly destroyRef = inject(DestroyRef);

  title: InputSignal<string> = input<string>('');
  content: InputSignal<string> = input<string>('');
  allowEmpty: InputSignal<boolean> = input<boolean>(false);
  disableSaveButton = input<boolean>(false);
  /**
   * When true, shows the Edit control in read-only mode (admin / country admin in scope).
   */
  canEdit = input<boolean>(false);
  /**
   * Optional hint for Country Admin viewing a country outside their edit scope.
   */
  showReadOnlyLabel = input<boolean>(false);
  /** Adds the Country Information internal-link command to the editor ribbon. */
  allowInternalLinks: InputSignal<boolean> = input<boolean>(false);
  mode: ModelSignal<DataMode> = model(<DataMode>DataMode.readonly);

  save: OutputEmitterRef<string> = output();
  dismiss: OutputEmitterRef<void> = output();
  modeChange: OutputEmitterRef<DataMode> = output();

  editor: Signal<EditorComponent | undefined> = viewChild(EditorComponent);

  hasNotChanges: WritableSignal<boolean> = signal(true);

  constructor() {
    this.activatedRouter.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.mode.set(DataMode.readonly);
        this.modeChange.emit(DataMode.readonly);
      });

    this.activeRichEditor.richEditor$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((richEditor: RichEdit | undefined) => {
        if (richEditor) {
          const content = richEditor.document.getText();
          this.hasNotChanges.set(
            !this.activeRichEditor.hasFileUnsavedChanges ||
              (!this.allowEmpty() && content.replace(/[\r\n\s]/g, '') === '')
          );
        }
      });
  }

  get readonly(): DataMode {
    return DataMode.readonly;
  }

  get editMode(): DataMode {
    return DataMode.edition;
  }

  ngAfterViewInit(): void {
    if (this.allowEmpty() && this.editor()) {
      this.hasNotChanges.set(this.activeRichEditor.isEmpty);
    }
  }

  ngOnDestroy(): void {
    this.activeRichEditor.destroy();
  }

  openUnsaveChangeWarningModal(
    isDisabledSaveButton?: boolean
  ): ComponentRef<ModalComponent> {
    return this.editor()!.openUnsaveChangeWarningModal(isDisabledSaveButton);
  }

  edit(): void {
    this.mode.set(DataMode.edition);
    this.modeChange.emit(DataMode.edition);
    this.activeRichEditor.setMode(DataMode.edition);
  }

  cancelHandler(): void {
    this.dismiss.emit();
  }

  saveHandler(): void {
    this.activeRichEditor.extractHTML((html: string) => {
      this.save.emit(html);
    });
  }
}
